// useScrollTracking.js - FIXED FOR NAV CLICKS
import { useEffect, useRef } from 'react';

const useScrollTracking = (sections) => {
  const trackedSections = useRef(new Set());
  const isScrolling = useRef(false);
  const isProgrammaticScroll = useRef(false);
  const scrollTimeout = useRef(null);

  useEffect(() => {
    const checkVisibleSections = () => {
      // Don't track during programmatic scroll (nav clicks)
      if (isProgrammaticScroll.current) return;

      const search = window.location.search;
      const variant = new URLSearchParams(search).get("v");

      sections.forEach((section) => {
        if (trackedSections.current.has(section.id)) return;

        const element = document.getElementById(section.id);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Section is in the middle of viewport (user is actually viewing it)
        const isVisible = (
          rect.top < windowHeight * 0.5 &&
          rect.bottom > windowHeight * 0.5
        );

        const hasHeight = rect.height > 0;

        if (isVisible && hasHeight) {
          if (window.gtag) {
            window.gtag('event', 'section_view', {
              section_name: section.eventName,
              variant: variant
            });

          }

          trackedSections.current.add(section.id);
        }
      });
    };

    // Detect programmatic scroll (nav clicks)
    const detectProgrammaticScroll = () => {
      isProgrammaticScroll.current = true;

      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Wait for scroll to finish, then check what's visible
      scrollTimeout.current = setTimeout(() => {
        isProgrammaticScroll.current = false;
        checkVisibleSections(); // Only check once scroll stops
      }, 800); // Wait 800ms after last scroll event
    };

    // Debounced manual scroll handler
    const handleScroll = () => {
      detectProgrammaticScroll();

      if (isScrolling.current || isProgrammaticScroll.current) return;

      isScrolling.current = true;
      requestAnimationFrame(() => {
        checkVisibleSections();
        isScrolling.current = false;
      });
    };

    // Initial check
    const initialTimer = setTimeout(checkVisibleSections, 300);

    // Listen to scroll
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', checkVisibleSections, { passive: true });

    return () => {
      clearTimeout(initialTimer);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkVisibleSections);
    };
  }, [sections]);

  return null;
};

export default useScrollTracking;